import { getAuth } from 'firebase/auth';
import Bugsnag from '@bugsnag/js';

const SESSION_STORAGE_KEY = 'gobo_session_id';

/**
 * Generates a unique session ID in the format: {timestamp}_{randomString}
 * Matches the legacy Vue app's session ID format.
 */
function generateSessionId(): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 10);
  return `${timestamp}_${random}`;
}

/**
 * Gets or creates a session ID stored in sessionStorage.
 * The session ID persists across page navigations but resets on new browser sessions.
 */
function getSessionId(): string {
  if (typeof window === 'undefined') {
    return 'server-side';
  }

  let sessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);

  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  }

  return sessionId;
}

/**
 * Gets the current user's Firebase ID token for API authentication.
 */
async function getAuthToken(): Promise<string | null> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    return null;
  }

  try {
    return await user.getIdToken();
  } catch (error) {
    console.error('[UserLogger] Failed to get auth token:', error);
    return null;
  }
}

/**
 * Core logging function that sends activity to the API.
 * Handles errors silently to avoid breaking app functionality.
 */
async function logActivity(action: string, metadata: Record<string, unknown> = {}): Promise<void> {
  try {
    const token = await getAuthToken();

    if (!token) {
      console.warn('[UserLogger] No auth token available, skipping log');
      return;
    }

    const userAgent = typeof window !== 'undefined' ? navigator.userAgent : 'server-side';
    const sessionId = getSessionId();

    const response = await fetch('/api/log', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        action,
        metadata,
        userAgent,
        sessionId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Logging API error: ${response.status} - ${JSON.stringify(errorData)}`);
    }
  } catch (error) {
    console.error('[UserLogger] Failed to log activity:', error);

    // Report to Bugsnag but don't throw
    if (typeof window !== 'undefined') {
      Bugsnag.notify(error as Error, (event) => {
        event.context = 'user-logging';
        event.severity = 'warning';
        event.addMetadata('logging', { action, metadata });
      });
    }
  }
}

/**
 * User activity logger with convenience methods for common actions.
 * All methods handle errors silently and report to Bugsnag.
 */
export const userLogger = {
  /**
   * Log a generic activity with custom action and metadata.
   */
  logActivity,

  // ============ Authentication ============

  /**
   * Log a successful user login.
   */
  async logLogin(email: string): Promise<void> {
    await logActivity('login', {
      email,
      loginMethod: 'firebase_auth',
    });
  },

  /**
   * Log a user logout.
   */
  async logLogout(): Promise<void> {
    await logActivity('logout', {});
  },

  // ============ Events ============

  /**
   * Log when a user creates a new event.
   */
  async logEventCreated(eventId: string, eventTitle: string, eventType: string): Promise<void> {
    await logActivity('event_created', {
      eventId,
      eventTitle,
      eventType,
    });
  },

  /**
   * Log when a user publishes an event.
   */
  async logEventPublished(eventId: string, eventTitle: string): Promise<void> {
    await logActivity('event_published', {
      eventId,
      eventTitle,
    });
  },

  /**
   * Log when a user deletes an event.
   */
  async logEventDeleted(eventId: string, eventTitle: string): Promise<void> {
    await logActivity('event_deleted', {
      eventId,
      eventTitle,
    });
  },

  /**
   * Log when a user duplicates an event.
   */
  async logEventDuplicated(
    newEventId: string,
    sourceEventId: string,
    eventTitle: string
  ): Promise<void> {
    await logActivity('event_duplicated', {
      newEventId,
      sourceEventId,
      eventTitle,
    });
  },

  // ============ Profiles ============

  /**
   * Log when a user creates an artist profile.
   */
  async logProfileCreated(artistId: string, artistName: string): Promise<void> {
    await logActivity('profile_created', {
      artistId,
      artistName,
    });
  },

  // ============ Venues ============

  /**
   * Log when a user creates a new venue.
   */
  async logVenueCreated(venueId: string, venueName: string): Promise<void> {
    await logActivity('venue_created', {
      venueId,
      venueName,
    });
  },

  /**
   * Log when a user claims an existing venue.
   */
  async logVenueClaimed(venueId: string, venueName: string): Promise<void> {
    await logActivity('venue_claimed', {
      venueId,
      venueName,
    });
  },

  // ============ Booking & Holds ============

  /**
   * Log when a user creates a hold.
   */
  async logHoldCreated(
    holdId: string,
    artistName: string,
    venueId: string,
    dates: string[]
  ): Promise<void> {
    await logActivity('hold_created', {
      holdId,
      artistName,
      venueId,
      dates,
    });
  },

  // ============ Offers ============

  /**
   * Log when a user creates an offer.
   */
  async logOfferCreated(
    offerId: string,
    bookingInquiryId: string,
    artistName: string
  ): Promise<void> {
    await logActivity('offer_created', {
      offerId,
      bookingInquiryId,
      artistName,
    });
  },

  /**
   * Log when a user sends an offer.
   */
  async logOfferSent(
    offerId: string,
    recipientEmail: string,
    bookingInquiryId: string
  ): Promise<void> {
    await logActivity('offer_sent', {
      offerId,
      recipientEmail,
      bookingInquiryId,
    });
  },

  // ============ Staff ============

  /**
   * Log when a user adds/invites staff.
   */
  async logStaffAdded(
    invitationId: string,
    email: string,
    role: string,
    entityId: string,
    entityType: 'venue' | 'promoter' | 'artist'
  ): Promise<void> {
    const idFieldMap: Record<string, string> = {
      venue: 'venueId',
      promoter: 'promoterId',
      artist: 'actId',
    };
    await logActivity('staff_added', {
      invitationId,
      email,
      role,
      [idFieldMap[entityType]]: entityId,
    });
  },

  // ============ Templates ============

  /**
   * Log when a user creates a template.
   */
  async logTemplateCreated(
    templateId: string,
    templateType: string,
    name: string
  ): Promise<void> {
    await logActivity('template_created', {
      templateId,
      templateType,
      name,
    });
  },
};

export default userLogger;
